import type { FC, ReactNode } from 'react'
import type { MarketCategoryCardProps } from '@/components/market/market-category-card'

import Link from 'next/link'

import { MarketCategoryCard } from '@/components/market/market-category-card'

/**
 * The Bazio market categories block: an intro column on the left (eyebrow,
 * title, hand-drawn red underline, outlined link) and the category cards in
 * one horizontal row on the right.
 *
 * Data-driven: every card is the same component, so the row always reads as
 * one set. Drop it on any page with `<MarketCategories />`, or pass a
 * different `items` list to reuse the card system elsewhere.
 */

/**
 * The official Bitcoin and Tether marks, overlapping and centred. Both are the
 * brands' own artwork (Bitcoin's orange coin with the white B, Tether's green
 * coin with the white double-bar T), scaled to a matched coin size.
 */
const CryptoArt = (
    <svg viewBox='0 0 124 72' className='mkt-svg'>
        <g transform='translate(41 36) scale(0.875) translate(-32 -32)'>
            <path fill='#f7931a' d='m63.033,39.744c-4.274,17.143-21.637,27.576-38.782,23.301-17.138-4.274-27.571-21.638-23.295-38.78,4.272-17.145,21.635-27.579,38.775-23.305,17.144,4.274,27.576,21.64,23.302,38.784z' />
            <path fill='#fff' d='m46.103,27.444c0.637-4.258-2.605-6.547-7.038-8.074l1.438-5.768-3.511-0.875-1.4,5.616c-0.923-0.23-1.871-0.447-2.813-0.662l1.41-5.653-3.509-0.875-1.439,5.766c-0.764-0.174-1.514-0.346-2.242-0.527l0.004-0.018-4.842-1.209-0.934,3.75s2.605,0.597,2.55,0.634c1.422,0.355,1.679,1.296,1.636,2.042l-1.638,6.571c0.098,0.025,0.225,0.061,0.365,0.117-0.117-0.029-0.242-0.061-0.371-0.092l-2.296,9.205c-0.174,0.432-0.615,1.08-1.609,0.834,0.035,0.051-2.552-0.637-2.552-0.637l-1.743,4.019,4.569,1.139c0.85,0.213,1.683,0.436,2.503,0.646l-1.453,5.834,3.507,0.875,1.439-5.772c0.958,0.26,1.888,0.5,2.798,0.726l-1.434,5.745,3.511,0.875,1.453-5.823c5.987,1.133,10.489,0.676,12.384-4.739,1.527-4.36-0.076-6.875-3.226-8.515,2.294-0.529,4.022-2.038,4.483-5.155zm-8.022,11.249c-1.085,4.36-8.426,2.003-10.806,1.412l1.928-7.729c2.38,0.594,10.012,1.77,8.878,6.317zm1.086-11.312c-0.99,3.966-7.1,1.951-9.082,1.457l1.748-7.01c1.982,0.494,8.365,1.416,7.334,5.553z' />
        </g>

        <g transform='translate(85 36) scale(1.75) translate(-16 -16)'>
            <circle cx='16' cy='16' r='16' fill='#26a17b' />
            <path fill='#fff' d='M17.922 17.383v-.002c-.11.008-.677.042-1.942.042-1.01 0-1.721-.03-1.971-.042v.003c-3.888-.171-6.79-.848-6.79-1.658 0-.809 2.902-1.486 6.79-1.66v2.644c.254.018.982.061 1.988.061 1.207 0 1.812-.05 1.925-.06v-2.643c3.88.173 6.775.85 6.775 1.658 0 .81-2.895 1.485-6.775 1.657m0-3.59v-2.366h5.414V7.819H8.595v3.608h5.414v2.365c-4.4.202-7.709 1.074-7.709 2.118 0 1.044 3.309 1.915 7.709 2.118v7.582h3.913v-7.584c4.393-.202 7.694-1.073 7.694-2.116 0-1.043-3.301-1.914-7.694-2.117' />
        </g>
    </svg>
)

const UsernameArt = (
    <span className='mkt-pill'>
        @alpha
    </span>
)

const YouTubeArt = (
    <svg viewBox='0 0 120 84' className='mkt-svg'>
        <rect x='6' y='6' width='108' height='72' rx='22' fill='#fff' />
        <path d='M50 28 78 42 50 56z' fill='var(--brand-red)' />
    </svg>
)

const AccountArt = (
    <svg viewBox='0 0 100 100' className='mkt-svg'>
        <circle cx='50' cy='50' r='48' fill='var(--ink-900)' />
        <circle cx='50' cy='50' r='36' fill='var(--brand-gray)' />
        <circle cx='50' cy='40' r='13' fill='var(--ink-900)' />
        <path d='M24 84c3.5-14 12.5-21 26-21s22.5 7 26 21z' fill='var(--ink-900)' />
    </svg>
)

const InfluencerArt = (
    <img className='mkt-eagle' src='/images/mta/eagle.svg' alt='' width={96} height={84} />
)

export const marketCategories: Array<MarketCategoryCardProps> = [
    {
        title: 'Crypto Deals',
        description: 'Verified market listings',
        icon: CryptoArt,
        href: '/browse?category=crypto-p2p',
        background: 'amber'
    },
    {
        title: 'Usernames',
        description: 'Premium social handles',
        icon: UsernameArt,
        href: '/browse?category=usernames',
        background: 'violet'
    },
    {
        title: 'YouTube Channels',
        description: 'Established creator properties',
        icon: YouTubeArt,
        href: '/browse?category=youtube-channels',
        background: 'red'
    },
    {
        title: 'Accounts',
        description: 'Social media account listings',
        icon: AccountArt,
        href: '/browse?category=social-accounts',
        background: 'sky'
    },
    {
        title: 'Influencers',
        description: 'Discover & connect with top creators',
        icon: InfluencerArt,
        href: '/browse?category=influencers',
        background: 'albania'
    }
]

interface MarketCategoriesProps {
    items?: Array<MarketCategoryCardProps>
    /** Eyebrow above the title. */
    eyebrow?: string
    /** Large display title in the intro column. */
    title?: string
    /** Outlined link under the title. */
    actionLabel?: string
    actionHref?: string
    className?: string
}

export const MarketCategories: FC<MarketCategoriesProps> = ({
    items = marketCategories,
    eyebrow = 'Explore',
    title = 'Bazio',
    actionLabel = 'View all categories',
    actionHref = '/browse',
    className
}): ReactNode => (
    <div className={className ? `mkt-block ${className}` : 'mkt-block'}>
        <div className='mkt-intro'>
            <p className='mkt-eyebrow'>
                {eyebrow}
            </p>

            <h2 className='mkt-heading'>
                {title}
            </h2>

            <svg className='mkt-underline' viewBox='0 0 320 30' aria-hidden='true'>
                <path
                    d='M3 24c58-9 122-15 186-17 43-1 88 0 128 4-40 2-84 3-127 5-63 3-127 9-185 19z'
                    fill='var(--brand-red)'
                />
                <path
                    d='M28 29c74-11 156-17 240-17-84 4-165 10-240 17z'
                    fill='var(--brand-red)'
                    opacity='.72'
                />
            </svg>

            <Link className='mkt-cta' href={actionHref}>
                {actionLabel}

                <svg viewBox='0 0 34 14' aria-hidden='true'>
                    <path
                        d='M1 7h30M25 1.5 31.5 7 25 12.5'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.6'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                    />
                </svg>
            </Link>
        </div>

        <div className='mkt-rail'>
            <ul className='mkt-row'>
                {items.map((item) => (
                    <li key={item.title}>
                        <MarketCategoryCard {...item} />
                    </li>
                ))}
            </ul>
        </div>
    </div>
)
